import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { driverLicenseActions } from './driver_license.actions';
import { environment } from '../../../../environments/environment';
import { DriverLicenseExtraction, UploadedDriverLicense, ViewedDriverLicense } from '../../../models/document.model';

@Injectable()
export class DriverLicenseEffects {
    private httpClient = inject(HttpClient);
    private actions$ = inject(Actions);
    private store = inject(Store);

    getPresignedUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.getPresignedUrl),
            exhaustMap(({ filename }) => {
                let params = new HttpParams().set('filename', filename);
                return this.httpClient.get<string>(`${environment.api}/document/driver_license/insertion_url`, { params }).pipe(
                    map((payload) => driverLicenseActions.getPresignedUrlSuccess({ payload })),
                    catchError((error) => {
                        this.store.dispatch(driverLicenseActions.getPresignedUrlFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    uploadS3$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.uploadS3),
            exhaustMap(({ data, url }) => {
                let headers = new HttpHeaders();
                headers = headers.set('Content-Type', data.type || 'application/octet-stream');
                return this.httpClient.put(url, data, { headers }).pipe(
                    map(() => driverLicenseActions.uploadS3Success()),
                    catchError((error) => {
                        this.store.dispatch(driverLicenseActions.uploadS3Fail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    uploadBackend$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.uploadBackend),
            exhaustMap(({ data, file, customer_id, file_type, timestamp }) => {
                let req;
                if (file_type === 'pdf') {
                    const formData = new FormData();
                    formData.append('file', file!);
                    const params = new HttpParams().set('customer_id', customer_id!.toString());
                    req = this.httpClient.post<UploadedDriverLicense>(
                        `${environment.api}/document/driver_license/`,
                        formData,
                        { params },
                    );
                } else {
                    const params = new HttpParams().set('customer_id', data!.customer_id.toString());
                    req = this.httpClient.post<UploadedDriverLicense>(
                        `${environment.api}/document/driver_license/image/path`,
                        { file_path: `driver_license/${timestamp}/` },
                        { params },
                    );
                }
                return req.pipe(
                    map((payload) => driverLicenseActions.uploadBackendSuccess({
                        payload: this.normalizeUploadedDriverLicense(payload),
                    })),
                    catchError((error) => {
                        this.store.dispatch(driverLicenseActions.uploadBackendFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    extract$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.extract),
            exhaustMap(({ driver_license_id }) => {
                const params = new HttpParams().set('driver_license_id', driver_license_id.toString());
                return this.httpClient.post(`${environment.api}/document/driver_license/extract_ws`, null, { params }).pipe(
                    map(() => driverLicenseActions.extractSuccess()),
                    catchError((error) => {
                        this.store.dispatch(driverLicenseActions.extractFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );


    editExtraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.editExtraction),
            exhaustMap(({ extracted_id, data }) => {
                return this.httpClient
                    .put(`${environment.api}/document/driver_license/modify_extraction/${extracted_id}`, data)
                    .pipe(map(() => driverLicenseActions.editExtractionSuccess({ payload: data })));
            })
        )
    );

    loadExtractionInfo$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.loadExtractionInfo),
            exhaustMap(({ extracted_id }) => {
                return this.httpClient
                    .get<any>(`${environment.api}/document/driver_license/extraction_info/${extracted_id}`)
                    .pipe(
                        map((payload) => {
                            // Il backend restituisce DriverLicenseExtraction piatta, non ViewedDriverLicense.
                            // extracted_driver_license_id è garantito dal path param se il backend non lo include nel body.
                            const wrapped: ViewedDriverLicense = {
                                driver_license_id: payload.driver_license_id ?? 0,
                                customer_id: payload.customer_id ?? 0,
                                status: 'Estratta',
                                extracted_license: {
                                    ...(payload as DriverLicenseExtraction),
                                    extracted_driver_license_id: payload.extracted_driver_license_id ?? extracted_id,
                                },
                            };
                            return driverLicenseActions.loadExtractionInfoSuccess({ payload: wrapped });
                        }),
                        catchError((error) => {
                            this.store.dispatch(driverLicenseActions.loadExtractionInfoFail());
                            return throwError(() => new Error(error));
                        })
                    );
            })
        )
    );

    download$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.download),
            exhaustMap(({ ids }) => {
                return this.httpClient.post<string[]>(`${environment.api}/document/driver_license/download_links`, ids).pipe(
                    map((payload) => driverLicenseActions.downloadSuccess({ payload }))
                );
            })
        )
    );

    deleteDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.deleteDocument),
            exhaustMap(({ id }) => {
                return this.httpClient.delete(`${environment.api}/document/driver_license/${id}`).pipe(
                    map(() => driverLicenseActions.deleteDocumentSuccess()),
                    catchError((error) => {
                        this.store.dispatch(driverLicenseActions.deleteDocumentFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    deleteExtraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.deleteExtraction),
            exhaustMap(({ extracted_id }) => {
                return this.httpClient.delete(`${environment.api}/document/driver_license/extraction/${extracted_id}`).pipe(
                    map(() => driverLicenseActions.deleteExtractionSuccess()),
                    catchError((error) => {
                        this.store.dispatch(driverLicenseActions.deleteExtractionFail());
                        return throwError(() => new Error(error));
                    })
                );
            })
        )
    );

    getCSV$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.getCSV),
            exhaustMap(({ ids }) => {
                let params = new HttpParams();
                ids.forEach(id => params = params.append('ids', id.toString()));
                return this.httpClient.get(`${environment.api}/document/driver_license/csv`, { params, responseType: 'text' }).pipe(
                    map((payload) => driverLicenseActions.getCSVSuccess({ payload }))
                );
            })
        )
    );

    getExcel$ = createEffect(() =>
        this.actions$.pipe(
            ofType(driverLicenseActions.getExcel),
            exhaustMap(({ ids }) => {
                let params = new HttpParams();
                ids.forEach(id => params = params.append('ids', id.toString()));
                return this.httpClient.get(`${environment.api}/document/driver_license/excel`, { params, responseType: 'blob' }).pipe(
                    map((payload) => driverLicenseActions.getExcelSuccess({ payload }))
                );
            })
        )
    );

    private normalizeUploadedDriverLicense(payload: unknown): UploadedDriverLicense {
        if (typeof payload === 'number') {
            return { driver_license_id: payload };
        }
        const p = payload as Record<string, unknown>;
        const id = p['driver_license_id'] ?? p['id'];
        return { driver_license_id: id as number };
    }
}
